import { format, parseISO } from 'date-fns';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { DailyUsageTrend } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface LynAITrendChartProps {
  data: DailyUsageTrend[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  // Find the full data from payload
  const inputTokens = payload.find((p) => p.dataKey === 'inputTokens')?.value || 0;
  const outputTokens = payload.find((p) => p.dataKey === 'outputTokens')?.value || 0;
  const totalTokens = inputTokens + outputTokens;

  // Get cost from the first payload item's parent data
  const dataPoint = payload[0] as unknown as {
    payload: { cost: number; prompts: number; fullDate: string };
  };

  return (
    <div className='bg-white p-3 border rounded shadow-lg'>
      <p className='font-medium text-sm mb-2'>{dataPoint.payload.fullDate}</p>
      <div className='space-y-1'>
        <p className='text-sm'>
          <span
            className='inline-block w-3 h-3 rounded-sm mr-2'
            style={{ backgroundColor: '#3b82f6' }}
          />
          Input: {formatNumber(inputTokens)}
        </p>
        <p className='text-sm'>
          <span
            className='inline-block w-3 h-3 rounded-sm mr-2'
            style={{ backgroundColor: '#10b981' }}
          />
          Output: {formatNumber(outputTokens)}
        </p>
        <p className='text-sm font-medium'>Total: {formatNumber(totalTokens)}</p>
        <p className='text-sm text-muted-foreground'>
          Cost: {formatCurrency(dataPoint.payload.cost)}
        </p>
        <p className='text-sm text-muted-foreground'>
          Prompts: {formatNumber(dataPoint.payload.prompts)}
        </p>
      </div>
    </div>
  );
};

const LynAITrendChart = ({ data }: LynAITrendChartProps) => {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Daily Usage Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[300px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    date: format(parseISO(item.date), 'MMM dd'),
    fullDate: item.date,
    inputTokens: item.total_input_tokens,
    outputTokens: item.total_output_tokens,
    totalTokens: item.total_tokens,
    cost: parseFloat(item.total_cost_usd),
    prompts: item.total_prompts,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-lg'>Daily Usage Trend (Input vs Output Tokens)</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width='100%' height={300}>
          <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='date' tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type='monotone'
              dataKey='inputTokens'
              name='Input Tokens'
              stackId='1'
              stroke='#3b82f6'
              fill='#3b82f6'
              fillOpacity={0.6}
            />
            <Area
              type='monotone'
              dataKey='outputTokens'
              name='Output Tokens'
              stackId='1'
              stroke='#10b981'
              fill='#10b981'
              fillOpacity={0.6}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default LynAITrendChart;
